import { z } from 'zod';

/**
 * Dob Desktop updates (docs/plans/PHASE_1_M7.md §3 "Auto-update", M7-7; owner answer O26 q7).
 *
 * The rules are pure so the app, the server and the tests share them:
 * - every PC takes versions from its location's **channel**: `test` (early) or `stable`;
 * - a new stable version reaches a **first share** of the PCs at once and the others after a
 *   number of hours (staged rollout); test versions reach their PCs at once;
 * - a PC installs only inside its location's **update window** (Baghdad time, default 04:00–05:00)
 *   and only once **every pending operation has been sent**;
 * - a PC that **missed** a window (switched off at night, like the warehouse PC, D73) updates at
 *   its next start-up, after sending its pending operations and before work begins;
 * - a company-wide **pause** (a bad version) stops every download and install at the next
 *   check-in, until it is lifted (owner request, D163).
 * Downloading may happen at any time; installing is what the window protects.
 */

export const UPDATE_CHANNELS = ['test', 'stable'] as const;
export type UpdateChannel = (typeof UPDATE_CHANNELS)[number];

/** What the server tells each PC at check-in (its location's settings, M7 plan §11). */
export interface DesktopUpdatePolicy {
  readonly channel: UpdateChannel;
  /** 0–23, Baghdad time. */
  readonly windowStartHour: number;
  /** 1–12. */
  readonly windowHours: number;
  /** Share of the PCs that get a new stable version first, in basis points (2500 = 25%). */
  readonly firstShareBp: number;
  /** Hours after a stable version is published before every other PC gets it. */
  readonly everyoneAfterHours: number;
  /** Company-wide stop: no download, no install while true (D163). */
  readonly paused: boolean;
}

export const DesktopUpdatePolicySchema = z.object({
  channel: z.enum(UPDATE_CHANNELS),
  windowStartHour: z.number().int().min(0).max(23),
  windowHours: z.number().int().min(1).max(12),
  firstShareBp: z.number().int().min(0).max(10_000),
  everyoneAfterHours: z.number().int().min(0).max(720),
  // A server from before D163 does not send it: not paused.
  paused: z.boolean().default(false),
});

/** The catalog defaults (settings `desktop_update.*`), used until a PC's first check-in. */
export const DEFAULT_UPDATE_POLICY: DesktopUpdatePolicy = {
  channel: 'stable',
  windowStartHour: 4,
  windowHours: 1,
  firstShareBp: 2_500,
  everyoneAfterHours: 48,
  paused: false,
};

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
/** Iraq keeps UTC+3 all year (no daylight saving). */
const BAGHDAD_OFFSET_MS = 3 * HOUR_MS;

export interface UpdateWindow {
  readonly start: Date;
  readonly end: Date;
}

/** The latest window start at or before `now`. */
function latestStart(now: Date, policy: DesktopUpdatePolicy): number {
  const local = now.getTime() + BAGHDAD_OFFSET_MS;
  const dayStartUtc = Math.floor(local / DAY_MS) * DAY_MS - BAGHDAD_OFFSET_MS;
  const today = dayStartUtc + policy.windowStartHour * HOUR_MS;
  return today <= now.getTime() ? today : today - DAY_MS;
}

/** The window `now` is in, or null. */
export function windowAt(now: Date, policy: DesktopUpdatePolicy): UpdateWindow | null {
  const start = latestStart(now, policy);
  const end = start + policy.windowHours * HOUR_MS;
  return now.getTime() < end ? { start: new Date(start), end: new Date(end) } : null;
}

/** The window `now` is in, or the next one. */
export function nextWindow(now: Date, policy: DesktopUpdatePolicy): UpdateWindow {
  const current = windowAt(now, policy);
  if (current) return current;
  const start = latestStart(now, policy) + DAY_MS;
  return { start: new Date(start), end: new Date(start + policy.windowHours * HOUR_MS) };
}

/** The end of the latest window that is over (strictly before or at `now`). */
export function lastWindowEnd(now: Date, policy: DesktopUpdatePolicy): Date {
  const start = latestStart(now, policy);
  const end = start + policy.windowHours * HOUR_MS;
  return new Date(end <= now.getTime() ? end : end - DAY_MS);
}

/**
 * A PC's place in the staged rollout, 0–9999, fixed for the device (FNV-1a of its id): the PCs
 * below the first share get a new stable version at once.
 */
export function rolloutBucket(deviceId: string): number {
  let hash = 0x811c9dc5;
  for (const char of deviceId.toLowerCase()) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash % 10_000;
}

/** Versions are plain `major.minor.patch` (the publish script refuses anything else). */
export const DESKTOP_VERSION = /^(0|[1-9]\d{0,3})\.(0|[1-9]\d{0,4})\.(0|[1-9]\d{0,5})$/;

function versionParts(version: string): [number, number, number] | null {
  const m = DESKTOP_VERSION.exec(version);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

/** True when `candidate` is a later version than `installed` (false if either is malformed). */
export function isNewerVersion(candidate: string, installed: string): boolean {
  const a = versionParts(candidate);
  const b = versionParts(installed);
  if (!a || !b) return false;
  for (let i = 0; i < 3; i += 1) {
    if (a[i] !== b[i]) return (a[i] ?? 0) > (b[i] ?? 0);
  }
  return false;
}

/** A version published on the PC's channel. */
export interface DesktopRelease {
  readonly version: string;
  /** When it was published on that channel (for stable: when it was promoted). */
  readonly releasedAt: Date;
}

/** When `release` becomes this PC's to install (staged rollout on the stable channel). */
export function releaseAvailableAt(
  release: DesktopRelease,
  policy: DesktopUpdatePolicy,
  deviceId: string,
): Date {
  if (policy.channel === 'test' || rolloutBucket(deviceId) < policy.firstShareBp) {
    return release.releasedAt;
  }
  return new Date(release.releasedAt.getTime() + policy.everyoneAfterHours * HOUR_MS);
}

/** `startup` until the PC has updated (or given up) after starting; then `running`. */
export type UpdateMoment = 'startup' | 'running';

export type UpdateStep =
  /** Updates are paused company-wide (D163): nothing is downloaded or installed. */
  | { readonly kind: 'paused' }
  | { readonly kind: 'up_to_date' }
  /** Staged rollout: this PC's turn comes later. */
  | { readonly kind: 'not_yet'; readonly availableAt: Date }
  /** `beforeWork`: at start-up after a missed window — the screen waits for it. */
  | { readonly kind: 'download'; readonly beforeWork: boolean }
  | { readonly kind: 'wait_window'; readonly window: UpdateWindow }
  | { readonly kind: 'send_first'; readonly pending: number; readonly beforeWork: boolean }
  | { readonly kind: 'install'; readonly reason: 'window' | 'missed_window' };

export interface UpdateStepInput {
  readonly moment: UpdateMoment;
  readonly now: Date;
  readonly policy: DesktopUpdatePolicy;
  readonly deviceId: string;
  readonly installedVersion: string;
  /** The latest version on the PC's channel, or null when none is known. */
  readonly release: DesktopRelease | null;
  /** The release is on disk and its checksum and release signature were verified. */
  readonly downloaded: boolean;
  /** Operations of this PC (for a hub: also those it holds for other PCs) not yet sent. */
  readonly pending: number;
}

/** What the PC should do about updates now. */
export function updateStep(input: UpdateStepInput): UpdateStep {
  const { release, now, policy } = input;
  if (policy.paused) return { kind: 'paused' };
  if (!release || !isNewerVersion(release.version, input.installedVersion)) {
    return { kind: 'up_to_date' };
  }
  const availableAt = releaseAvailableAt(release, policy, input.deviceId);
  if (availableAt.getTime() > now.getTime()) return { kind: 'not_yet', availableAt };

  const inWindow = windowAt(now, policy) !== null;
  // Missed: a whole window ended after the version became this PC's, and it is still not in.
  const missed =
    input.moment === 'startup' && availableAt.getTime() <= lastWindowEnd(now, policy).getTime();
  if (!inWindow && !missed) {
    return input.downloaded
      ? { kind: 'wait_window', window: nextWindow(now, policy) }
      : { kind: 'download', beforeWork: false };
  }
  const beforeWork = input.moment === 'startup';
  if (!input.downloaded) return { kind: 'download', beforeWork };
  if (input.pending > 0) return { kind: 'send_first', pending: input.pending, beforeWork };
  return { kind: 'install', reason: inWindow ? 'window' : 'missed_window' };
}
